import { SHOPIFY_CONFIG, SHOPIFY_GRAPHQL_URL } from '../config/shopify';
import { createSlug } from './commonService';

// GraphQL query to fetch products
const FETCH_PRODUCTS_QUERY = `
  query {
    products(first: 10) {
      edges {
        node {
          id
          title
          description
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 1) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    }
  }
`;

// GraphQL query to fetch single product by ID
const FETCH_PRODUCT_BY_ID_QUERY = `
  query getProduct($id: ID!) {
    product(id: $id) {
      id
      title
      description
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 5) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 10) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            availableForSale
          }
        }
      }
    }
  }
`;

// GraphQL mutation to create cart (replaces checkoutCreate)
const CREATE_CART_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        lines(first: 10) {
          edges {
            node {
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                }
              }
              attributes {
                key
                value
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const fetchShopifyProducts = async () => {
  try {
    const response = await fetch(SHOPIFY_GRAPHQL_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontAccessToken,
      },
      body: JSON.stringify({ 
        query: FETCH_PRODUCTS_QUERY 
      }),
    });

    const data = await response.json();
    
    console.log('🛍️ Shopify Response:', data);
    
    if (data.errors) {
      console.error('❌ Shopify API Error:', data.errors);
      throw new Error(data.errors[0]?.message || 'Failed to fetch products');
    }

    return data.data.products.edges;
  } catch (error) {
    console.error('❌ Error fetching Shopify products:', error);
    throw error;
  }
};

export const fetchProduct = async (productId) => {
  try {
    if (!productId) {
      throw new Error('Product ID is required');
    }

    const response = await fetch(SHOPIFY_GRAPHQL_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontAccessToken,
      },
      body: JSON.stringify({ 
        query: FETCH_PRODUCT_BY_ID_QUERY,
        variables: { id: productId }
      }),
    });

    const data = await response.json();
    
    console.log('🛍️ Shopify Product Response:', data);
    
    if (data.errors) {
      console.error('❌ Shopify API Error:', data.errors);
      throw new Error(data.errors[0]?.message || 'Failed to fetch product');
    }

    if (!data.data?.product) {
      throw new Error('Product not found');
    }

    return data.data.product;
  } catch (error) {
    console.error('❌ Error fetching Shopify product:', error);
    throw error;
  }
};

export const createShopifyCheckout = async (variantId, bookId, bookTitle, coverImageUrl, planName) => {
  try {
    const input = {
      attributes: [
        {
          key: "Book ID",
          value: bookId
        },
        {
          key: "Book Title",
          value: bookTitle || "Untitled Book"
        },
        {
          key: "Plan Code",
          value: createSlug(planName)
        }
      ],
      lines: [
        {
          merchandiseId: variantId,
          quantity: 1,
          attributes: [
            {
              key: "Book Title",
              value: bookTitle || "Untitled Book"
            }
          ]
        }
      ]
    };

    console.log('🛒 Creating cart with:', input);

    const response = await fetch(SHOPIFY_GRAPHQL_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontAccessToken,
      },
      body: JSON.stringify({
        query: CREATE_CART_MUTATION,
        variables: { input },
      }),
    });

    const data = await response.json();

    console.log('🛒 Cart Response:', data);

    if (data.errors) {
      console.error('❌ Cart API Error:', data.errors);
      throw new Error(data.errors[0]?.message || 'Failed to create cart');
    }

    if (data.data?.cartCreate?.userErrors?.length > 0) {
      console.error('❌ Cart User Errors:', data.data.cartCreate.userErrors);
      throw new Error(data.data.cartCreate.userErrors[0]?.message || 'Cart validation failed');
    }

    const checkoutUrl = data.data.cartCreate.cart.checkoutUrl;
    console.log('✅ Checkout URL generated:', checkoutUrl);
    
    return checkoutUrl;
  } catch (error) {
    console.error('❌ Error creating cart:', error);
    throw error;
  }
};